import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiArrowUpRight } from 'react-icons/fi';
import Navbar from '../Components/Navbar2';
import { StoriesData } from '../Components/storiesMetaData';
import CompanyFilm from './CompanyFilm';
import CustomerVoices from './CustomerVoices';
import VoicesCustomerMarks from './VoicesCustomerMarks';
import voicesMedia from './voicesMedia.json';
import { CraftJourney, EndingJourney, HeroJourney } from './VoicesJourneys';
import { clamp, smooth, useMotionPreference } from './useScrollScene';
import VoicesStoryTransition from './VoicesStoryTransition';
import { HomeExpertise, HomeMeaningGuide } from './VoicesHomeMeaning';

const { platforms } = voicesMedia;

const stages = [
  { label: 'Build', eyebrow: 'Custom AI agents', title: 'Built around', emphasis: 'your work.', description: 'Build an agent around a useful job.', link: '/ai-agents', cta: 'Explore custom agents' },
  { label: 'Connect', eyebrow: 'Salesforce & Agentforce', title: 'Connected.', emphasis: 'Not complicated.', description: 'Bring the agent into the systems your team already uses.', link: '/services/salesforce-agentforce', cta: 'Explore Agentforce' },
  { label: 'Stay', eyebrow: 'Managed agents', title: 'Launch is', emphasis: 'only the start.', description: 'Keep a human owner. Review outcomes and improve after launch.', link: '/managed-agents', cta: 'Meet your long-term team' },
];
const featured = [
  { slug: 'trivago-support-portal', image: '/assets/voices/trivago-2400.webp', srcSet: '/assets/voices/trivago.webp 1000w, /assets/customer-stories/trivago/hero.webp 1800w, /assets/voices/trivago-2400.webp 2400w', label: 'trivago', title: 'Less searching.', emphasis: 'More moving forward.', description: 'A multilingual self-service foundation for Trivago\u2019s hotel partners.', subtitle: 'Experience Cloud \u00b7 Customer self-service' },
  { slug: '24-hour-home-care-workforce-modernization', image: '/assets/voices/24-hour-home-care-original.webp', srcSet: '/assets/voices/24-hour-home-care.webp 1000w, /assets/voices/24-hour-home-care-original.webp 1537w', label: '24 Hour Home Care', title: 'A simpler day.', emphasis: 'For the people who care.', description: 'A calmer caregiver experience and a lighter platform foundation.', subtitle: 'Field Service \u00b7 Connected care' },
];
const furtherStories = ['making-arr-easier-to-explain', 'unified-patient-provider-digital-ecosystem', 'proven-revenue-smartly-delivered'].map(slug => StoriesData.find(story => story.url === slug)).filter(Boolean);

export default function VoicesHome() {
  const { hash } = useLocation();
  const [reduced, toggleMotion] = useMotionPreference();
  const storyGrid = useRef(null);

  useEffect(() => {
    const cards = Array.from(storyGrid.current?.querySelectorAll('.v-story') || []);
    if (reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom < -80 || rect.top > window.innerHeight + 80) return;
        const entered = smooth(0, 1, clamp((window.innerHeight * .94 - rect.top) / (window.innerHeight * .70)));
        card.style.transform = `perspective(1400px) translateY(${(1 - entered) * (32 + index * 8)}px) rotateX(${(1 - entered) * 5}deg) scale(${.955 + .045 * entered})`;
        card.querySelector('img').style.transform = `scale(${1.08 - .08 * entered})`;
      });
    };
    const wake = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', wake, { passive: true });
    window.addEventListener('resize', wake, { passive: true });
    wake();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', wake);
      window.removeEventListener('resize', wake);
      cards.forEach(card => { card.style.transform = ''; card.querySelector('img').style.transform = ''; });
    };
  }, [reduced]);

  useEffect(() => {
    if (!hash) return;
    // Deep links arrive before React creates their targets and App resets the scroll.
    const frame = requestAnimationFrame(() => {
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
      document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return <>
    <VoicesStoryTransition reducedMotion={reduced} />
    <Navbar />
    <main className="v-home" id="voices-main">
      <HeroJourney reduced={reduced} toggleMotion={toggleMotion} featured={featured} />

      <section className="v-section v-more-work" id="customer-stories">
        <div className="v-section-heading">
          <div><p className="v-label">The story continues</p><h2 className="v-section-title">Different businesses.<br /><em>The same care.</em></h2></div>
          <Link to="/work" className="v-text-link">All customer stories <FiArrowUpRight aria-hidden="true" /></Link>
        </div>
        <div className="v-story-grid" ref={storyGrid}>{furtherStories.map(story => <Link className="v-story" key={story.url} to={`/customer-stories/${story.url}`}>
          <div className="v-story-image"><img src={story.backgroundImage} alt={story.customer} loading="lazy" /><FiArrowUpRight aria-hidden="true" /></div>
          <div className="vh-story-copy"><p className="v-label">{story.customer}</p><h3>{story.headline}</h3><span className="v-text-link">Read the story <FiArrowUpRight aria-hidden="true" /></span></div>
        </Link>)}</div>
      </section>

      <VoicesCustomerMarks />

      <CompanyFilm reducedMotion={reduced} />

      <CraftJourney reduced={reduced} stages={stages} />
      <HomeMeaningGuide />
      <HomeExpertise />

      <section className="v-platforms v-section" aria-labelledby="v-platforms-title">
        <p className="v-label">Our deep CRM and data expertise</p>
        <h2 className="v-section-title" id="v-platforms-title">Working with the systems<br /><em>you already depend on.</em></h2>
        <div className="v-logo-grid">{platforms.map(platform => <div key={platform.name}><img src={platform.src} alt={platform.name} loading="lazy" decoding="async" /></div>)}</div>
        <p className="vh-platform-note">Technologies in our service portfolio. Logos do not imply a certification, vendor endorsement or customer relationship.</p>
      </section>

      <CustomerVoices reducedMotion={reduced} />

      <EndingJourney reduced={reduced} />
    </main>
  </>;
}
